import { useQueries } from '@tanstack/react-query'
import { useParams } from 'next/navigation'
import dayjs from 'lib/dayjs'
import type { GetRoomPlanAvailabilityResponse } from 'tokyu-hooks/lib/response/hotel'
import {
  FETCH_ROOM_PLAN_AVAILABILITY,
  fetchRoomPlanAvailability,
} from './fetchRoomPlanAvailability'
import useAuthentication from '../useAuthentication'

interface UseQueryRoomPlanAvailabilityParams {
  hotelId?: string
  roomPlanCode?: string
  nights: number
  adults: number
  children: number
  // Months to load, as 'YYYY-MM'. One query (and one cache entry) per month,
  // so paging to an already-fetched month renders instantly from cache and
  // only the newly revealed month hits the network.
  months: string[]
}

interface UseQueryRoomPlanAvailabilityResult {
  // room_rates merged across all loaded months.
  data: GetRoomPlanAvailabilityResponse['data'] | null
  isLoading: boolean
  isError: boolean
}

function useQueryRoomPlanAvailability({
  hotelId,
  roomPlanCode,
  nights,
  adults,
  children,
  months,
}: UseQueryRoomPlanAvailabilityParams): UseQueryRoomPlanAvailabilityResult {
  const params = useParams<{ locale: string }>()
  const { locale } = params || {}
  const { withTriplaApiRequest } = useAuthentication()

  const results = useQueries({
    queries: months.map((yearMonth) => {
      const startDate = dayjs(`${yearMonth}-01`)
        .startOf('month')
        .format('YYYY-MM-DD')
      const endDate = dayjs(`${yearMonth}-01`)
        .endOf('month')
        .format('YYYY-MM-DD')

      return {
        queryKey: [
          FETCH_ROOM_PLAN_AVAILABILITY,
          {
            hotelId,
            roomPlanCode,
            nights,
            adults,
            children,
            startDate,
            endDate,
            locale,
          },
        ],
        queryFn: withTriplaApiRequest(fetchRoomPlanAvailability),
        enabled: !!hotelId && !!roomPlanCode,
        // Mirrors useQueryAvailabilityCalendar: dedupe per-navigation
        // refetches of the same month; the room-plan detail/payment pages
        // re-validate real availability at booking time.
        staleTime: 60 * 1000,
      }
    }),
  })

  const loaded = results
    .map((result) => result.data)
    .filter((data): data is GetRoomPlanAvailabilityResponse['data'] => !!data)

  const data =
    loaded.length === 0
      ? null
      : {
          room_rates: Object.assign(
            {},
            ...loaded.map((item) => item.room_rates),
          ),
          promotions: Object.assign(
            {},
            ...loaded.map((item) => item.promotions),
          ),
          reservable_period_end_date:
            loaded.find((item) => item.reservable_period_end_date)
              ?.reservable_period_end_date ?? null,
        }

  return {
    data,
    isLoading: results.some((result) => result.isLoading),
    isError: results.every((result) => result.isError) && results.length > 0,
  }
}

export default useQueryRoomPlanAvailability
